import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { RiWhatsappFill, RiPhoneFill } from 'react-icons/ri';
import authApi from '../services/authApi';
import '../sass/AdminPage.scss';

const STATUS_LABEL = {
  PENDING:  { label: 'Pendiente',  cls: 'badge-pending'  },
  ACTIVE:   { label: 'Activa',     cls: 'badge-active'   },
  REJECTED: { label: 'Rechazada',  cls: 'badge-rejected' },
  DELETED:  { label: 'Eliminada',  cls: 'badge-deleted'  },
};

function formatPrice(p) {
  if (!p) return '—';
  return '$' + Number(p).toLocaleString('es-CL');
}

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || { label: status, cls: 'badge-pending' };
  return <span className={`adm-badge ${s.cls}`}>{s.label}</span>;
}

// ── Modal de vista previa ─────────────────────────────────────────────────────
function ListingPreviewModal({ listing, onClose }) {
  const [imgIdx, setImgIdx] = useState(0);
  const photos = listing.imageUrls || [];
  const img = photos[imgIdx];

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const title = [listing.makerName, listing.modelName, listing.year].filter(Boolean).join(' ') || 'Sin título';

  const specs = [
    { icon: '📅', label: 'Año',         value: listing.year },
    { icon: '🔢', label: 'Kilómetros',  value: listing.mileage ? Number(listing.mileage).toLocaleString('es-CL') + ' km' : null },
    { icon: '⚙️', label: 'Transmisión', value: listing.transmission },
    { icon: '⛽', label: 'Combustible', value: listing.fuelType },
    { icon: '📍', label: 'Ciudad',      value: listing.city },
    { icon: '🗺️', label: 'Región',      value: listing.region },
    { icon: '🚗', label: 'Condición',   value: listing.condition === 'NEW' ? 'Nuevo' : listing.condition === 'USED' ? 'Usado' : null },
  ].filter(s => s.value);

  const cleanPhone = (p) => p ? p.replace(/\D/g, '') : null;
  const waUrl = () => {
    const clean = cleanPhone(listing.contactPhone);
    if (!clean) return null;
    const num = clean.startsWith('56') ? clean : `56${clean.startsWith('9') ? clean : '9' + clean}`;
    return `https://wa.me/${num}?text=${encodeURIComponent(`Hola, me interesa tu auto: ${title}`)}`;
  };

  return (
    <div className="preview-overlay" onClick={onClose}>
      <div className="preview-modal" onClick={e => e.stopPropagation()}>

        <div className="preview-modal-header">
          <div>
            <span className="preview-label">Vista previa</span>
            <StatusBadge status={listing.status} />
          </div>
          <button className="preview-close" onClick={onClose}>✕</button>
        </div>

        <div className="preview-body">
          {/* Fotos */}
          <div className="preview-photos">
            {photos.length > 0 ? (
              <>
                <div className="preview-main-img">
                  <img
                    src={img}
                    alt="foto"
                    onError={e => { e.target.src = 'https://placehold.co/800x450/1a1a1a/fff?text=Sin+foto'; }}
                  />
                  {photos.length > 1 && (
                    <>
                      <button className="prev-arrow left" onClick={() => setImgIdx(i => (i - 1 + photos.length) % photos.length)}>‹</button>
                      <button className="prev-arrow right" onClick={() => setImgIdx(i => (i + 1) % photos.length)}>›</button>
                      <span className="prev-counter">{imgIdx + 1} / {photos.length}</span>
                    </>
                  )}
                </div>
                {photos.length > 1 && (
                  <div className="preview-thumbs">
                    {photos.map((src, i) => (
                      <img
                        key={i} src={src} alt={`foto-${i}`}
                        className={i === imgIdx ? 'active' : ''}
                        onClick={() => setImgIdx(i)}
                        onError={e => { e.target.src = 'https://placehold.co/160x100/1a1a1a/fff?text=' + (i+1); }}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="preview-no-photo">📷 Sin fotos</div>
            )}
          </div>

          {/* Info */}
          <div className="preview-info">
            <h3 className="preview-title">{title}</h3>
            <div className="preview-price">{formatPrice(listing.price)}</div>

            <div className="preview-specs">
              {specs.map((s, i) => (
                <div key={i} className="spec-chip">
                  <span className="spec-icon">{s.icon}</span>
                  <span className="spec-label">{s.label}</span>
                  <span className="spec-value">{s.value}</span>
                </div>
              ))}
            </div>

            {listing.description && (
              <div className="preview-desc">
                <strong>Descripción</strong>
                <p>{listing.description}</p>
              </div>
            )}

            <div className="preview-contact">
              <strong>Contacto</strong>
              <div className="contact-name">{listing.contactName || '—'}</div>
              <div className="contact-phone">{listing.contactPhone || '—'}</div>
              <div className="contact-btns">
                {waUrl() && (
                  <a href={waUrl()} target="_blank" rel="noopener noreferrer" className="btn-wa">
                    <RiWhatsappFill /> WhatsApp
                  </a>
                )}
                {listing.contactPhone && (
                  <a href={`tel:${cleanPhone(listing.contactPhone)}`} className="btn-call">
                    <RiPhoneFill /> Llamar
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ROLE_LABEL = { ADMIN: 'Admin', REVIEWER: 'Revisor', USER: 'Usuario' };
const ROLE_CLS   = { ADMIN: 'badge-active', REVIEWER: 'badge-pending', USER: 'badge-deleted' };

// ── Panel de usuarios ─────────────────────────────────────────────────────────
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    authApi.get('/api/admin/users')
      .then(r => setUsers(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeRole = async (targetUid, role) => {
    setSaving(targetUid);
    try {
      await authApi.post('/api/admin/roles', null, { params: { targetUid, role } });
      setMsg('Rol actualizado.');
      load();
      setTimeout(() => setMsg(''), 3000);
    } catch { setMsg('Error al guardar.'); }
    finally { setSaving(null); }
  };

  return (
    <div className="adm-section">
      <h3>Usuarios registrados</h3>
      {msg && <div className="adm-msg">{msg}</div>}

      <table className="adm-table">
        <thead>
          <tr><th>Usuario</th><th>Email</th><th>Último login</th><th>Rol</th></tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.uid}>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {u.photoUrl
                    ? <img src={u.photoUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} referrerPolicy="no-referrer" />
                    : <span style={{ width: 28, height: 28, borderRadius: '50%', background: '#e63a3a', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
                        {(u.displayName || u.email || '?')[0].toUpperCase()}
                      </span>
                  }
                  <span>{u.displayName || '—'}</span>
                </div>
              </td>
              <td style={{ fontSize: 13, color: '#555' }}>{u.email}</td>
              <td style={{ fontSize: 12, color: '#888' }}>
                {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('es-CL') : '—'}
              </td>
              <td>
                {u.role === 'ADMIN'
                  ? <span className="adm-badge badge-active">Admin</span>
                  : (
                    <select
                      className="select-status"
                      value={u.role}
                      disabled={saving === u.uid}
                      onChange={e => changeRole(u.uid, e.target.value)}
                    >
                      <option value="USER">Usuario</option>
                      <option value="REVIEWER">Revisor</option>
                    </select>
                  )
                }
              </td>
            </tr>
          ))}
          {users.length === 0 && (
            <tr><td colSpan={4} style={{ textAlign: 'center', color: '#aaa', padding: 30 }}>
              Aún no hay usuarios registrados.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Modal de edición de publicación ──────────────────────────────────────────
const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}`;

function EditListingModal({ listing, onClose, onSaved }) {
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    makerName:    listing.makerName    || '',
    modelName:    listing.modelName    || '',
    year:         listing.year         || '',
    condition:    listing.condition    || '',
    mileage:      listing.mileage      || '',
    transmission: listing.transmission || '',
    fuelType:     listing.fuelType     || '',
    price:        listing.price        || '',
    contactName:  listing.contactName  || '',
    contactPhone: listing.contactPhone || '',
    description:  listing.description  || '',
  });

  const [existingPhotos, setExistingPhotos] = useState(listing.imageUrls || []);
  const [newPhotos,      setNewPhotos]      = useState([]);
  const [newPreviews,    setNewPreviews]    = useState([]);
  const [deletingUrl,    setDeletingUrl]    = useState(null);
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const handleNewFiles = (files) => {
    const arr = Array.from(files);
    setNewPhotos(prev => [...prev, ...arr]);
    setNewPreviews(prev => [...prev, ...arr.map(f => URL.createObjectURL(f))]);
  };

  const removeNew = (idx) => {
    setNewPhotos(prev => prev.filter((_, i) => i !== idx));
    setNewPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const removeExisting = async (url) => {
    setDeletingUrl(url);
    try {
      const res = await fetch(
        `${API_BASE}/api/listings/${listing.id}/images?url=${encodeURIComponent(url)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error();
      setExistingPhotos(prev => prev.filter(u => u !== url));
    } catch {
      setErr('No se pudo eliminar la imagen.');
    } finally {
      setDeletingUrl(null);
    }
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const payload = {
        firebaseUid:  listing.firebaseUid,
        contactEmail: listing.contactEmail,
        makerId: null, makerName: form.makerName || null,
        modelId: null, modelName: form.modelName || null,
        year:         form.year         ? Number(form.year)    : null,
        condition:    form.condition    || null,
        mileage:      form.mileage      ? Number(form.mileage) : null,
        transmission: form.transmission || null,
        fuelType:     form.fuelType     || null,
        price:        form.price        ? Number(form.price)   : null,
        regionId:     listing.regionId  || null,
        cityId:       listing.cityId    || null,
        contactName:  form.contactName  || null,
        contactPhone: form.contactPhone || null,
        description:  form.description  || null,
      };
      const r1 = await fetch(`${API_BASE}/api/listings/${listing.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r1.ok) throw new Error();

      if (newPhotos.length > 0) {
        const fd = new FormData();
        newPhotos.forEach(f => fd.append('images', f));
        const r2 = await fetch(`${API_BASE}/api/listings/${listing.id}/images`, {
          method: 'POST', body: fd,
        });
        if (!r2.ok) throw new Error('Error al subir imágenes.');
      }

      onSaved();
      onClose();
    } catch (e) {
      setErr(e.message || 'No se pudo guardar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const F = ({ label, k, type = 'text', opts }) => (
    <div className="ef">
      <label>{label}</label>
      {opts ? (
        <select value={form[k]} onChange={e => set(k, e.target.value)}>
          <option value="">—</option>
          {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : (
        <input type={type} value={form[k]} onChange={e => set(k, e.target.value)} />
      )}
    </div>
  );

  const totalPhotos = existingPhotos.length + newPhotos.length;

  return (
    <div className="em-overlay" onClick={onClose}>
      <div className="em-sheet" onClick={e => e.stopPropagation()}>

        <div className="em-header">
          <span>Editar <strong>#{listing.id}</strong> — {[listing.makerName, listing.modelName].filter(Boolean).join(' ') || 'Sin título'}</span>
          <button className="em-close" onClick={onClose}>✕</button>
        </div>

        <div className="em-body">

          <p className="em-section">Vehículo</p>
          <div className="em-grid2">
            <F label="Marca"       k="makerName" />
            <F label="Modelo"      k="modelName" />
            <F label="Año"         k="year"    type="number" />
            <F label="Precio ($)"  k="price"   type="number" />
            <F label="Kilómetros"  k="mileage" type="number" />
            <F label="Condición"   k="condition" opts={[{ v:'USED',l:'Usado'},{v:'NEW',l:'Nuevo'}]} />
            <F label="Transmisión" k="transmission" opts={[{v:'Manual',l:'Manual'},{v:'Automático',l:'Automático'}]} />
            <F label="Combustible" k="fuelType" opts={[
              {v:'Bencina',l:'Bencina'},{v:'Diésel',l:'Diésel'},
              {v:'Híbrido',l:'Híbrido'},{v:'Eléctrico',l:'Eléctrico'},{v:'GLP / GNC',l:'GLP / GNC'},
            ]} />
          </div>

          <p className="em-section">Contacto</p>
          <div className="em-grid2">
            <F label="Nombre"    k="contactName" />
            <F label="Teléfono"  k="contactPhone" />
          </div>

          <div className="ef" style={{ marginTop: 12 }}>
            <label>Descripción</label>
            <textarea rows={3} value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <p className="em-section">Fotos <span className="em-photo-count">({totalPhotos} / 10)</span></p>
          <div className="em-photos">
            {existingPhotos.map(url => (
              <div key={url} className="em-thumb">
                <img src={url} alt="foto"
                  onError={e => { e.target.src='https://placehold.co/100x70/eee/aaa?text=?'; }} />
                <button className="em-rm" disabled={deletingUrl === url}
                  onClick={() => removeExisting(url)}>
                  {deletingUrl === url ? '…' : '✕'}
                </button>
              </div>
            ))}
            {newPreviews.map((src, i) => (
              <div key={i} className="em-thumb em-thumb-new">
                <img src={src} alt="nueva" />
                <button className="em-rm" onClick={() => removeNew(i)}>✕</button>
                <span className="em-new-badge">Nueva</span>
              </div>
            ))}
            {totalPhotos < 10 && (
              <button className="em-add-photo" onClick={() => fileInputRef.current?.click()}>
                <span>＋</span><small>Agregar</small>
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple
            style={{ display:'none' }} onChange={e => handleNewFiles(e.target.files)} />

          {err && <div className="em-error">{err}</div>}
        </div>

        <div className="em-footer">
          <button className="em-cancel" onClick={onClose} disabled={saving}>Cancelar</button>
          <button className="em-save"   onClick={save}    disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Panel de vehículos scrapeados ─────────────────────────────────────────────
function ScrapedPanel({ role }) {
  const [items, setItems]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [pages, setPages]           = useState(1);
  const [page, setPage]             = useState(0);
  const [q, setQ]                   = useState('');
  const [provider, setProvider]     = useState('');
  const [hiddenFilter, setHiddenFilter] = useState('visible'); // 'visible' | 'hidden' | 'all'
  const [loading, setLoading]       = useState(true);
  const [acting, setActing]         = useState(null);
  const [msg, setMsg]               = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: 30 };
      if (q) params.q = q;
      if (provider) params.provider = provider;
      if (hiddenFilter !== 'all') params.hidden = hiddenFilter === 'hidden';
      const res = await authApi.get('/api/admin/scraped', { params });
      setItems(res.data.content || []);
      setTotal(res.data.totalElements || 0);
      setPages(res.data.totalPages   || 1);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [page, q, provider, hiddenFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(0); }, [q, provider, hiddenFilter]);

  const notify = (text) => { setMsg(text); setTimeout(() => setMsg(''), 3000); };

  const act = async (id, endpoint, label) => {
    setActing(id);
    try {
      const res = await authApi.post(`/api/admin/scraped/${id}/${endpoint}`);
      if (!res.status || res.status >= 400) throw new Error(res.status);
      notify(label);
      load();
    } catch { notify('Error al realizar la acción.'); }
    finally { setActing(null); }
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar este vehículo permanentemente?')) return;
    setActing(id);
    try {
      const res = await authApi.delete(`/api/admin/scraped/${id}`);
      if (!res.status || res.status >= 400) throw new Error(res.status);
      notify('Vehículo eliminado.');
      load();
    } catch { notify('Error al eliminar.'); }
    finally { setActing(null); }
  };

  return (
    <div className="adm-section">
      {msg && <div className="adm-msg">{msg}</div>}

      <div className="adm-extra-filters">
        <input
          type="text" placeholder="Buscar por marca, modelo, título..."
          value={q} onChange={e => setQ(e.target.value)}
        />
        <input
          type="text" placeholder="Proveedor (chileautos, yapo...)"
          value={provider} onChange={e => setProvider(e.target.value)}
          style={{ maxWidth: 200 }}
        />
        <div className="adm-filter-row" style={{ margin: 0 }}>
          {[['all','Todos'],['visible','Visibles'],['hidden','Ocultos']].map(([val, label]) => (
            <button key={val}
              className={`btn-filter ${hiddenFilter === val ? 'active' : ''}`}
              onClick={() => setHiddenFilter(val)}
            >{label}</button>
          ))}
        </div>
        <span className="adm-count">{total} resultado{total !== 1 ? 's' : ''}</span>
      </div>

      {loading ? (
        <div className="adm-spinner-wrap"><div className="adm-spinner" /></div>
      ) : items.length === 0 ? (
        <div className="adm-empty">No hay vehículos con estos filtros.</div>
      ) : (
        <>
          <table className="adm-table">
            <thead>
              <tr>
                <th>ID</th><th>Vehículo</th><th>Precio</th><th>Proveedor</th>
                <th>Estado</th><th>Fecha</th><th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map(v => (
                <tr key={v.id} style={{ opacity: v.hidden ? 0.5 : 1 }}>
                  <td>#{v.id}</td>
                  <td>
                    <strong>{[v.makerName, v.modelName, v.year || ''].filter(Boolean).join(' ') || v.title || 'Sin título'}</strong>
                    {v.originalUrl && (
                      <div style={{ fontSize: 11 }}>
                        <a href={v.originalUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#888' }}>
                          Ver original ↗
                        </a>
                      </div>
                    )}
                  </td>
                  <td>{formatPrice(v.price)}</td>
                  <td style={{ fontSize: 13, textTransform: 'capitalize' }}>{v.providerName}</td>
                  <td>
                    {v.hidden
                      ? <span className="adm-badge badge-rejected">Oculto</span>
                      : <span className="adm-badge badge-active">Visible</span>
                    }
                  </td>
                  <td style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
                    {v.createdAt ? new Date(v.createdAt).toLocaleDateString('es-CL') : '—'}
                  </td>
                  <td>
                    <div className="adm-actions">
                      {v.hidden
                        ? <button className="btn-approve" disabled={acting === v.id} onClick={() => act(v.id, 'show', 'Vehículo visible nuevamente.')}>
                            ↑ Mostrar
                          </button>
                        : <button className="btn-reject" disabled={acting === v.id} onClick={() => act(v.id, 'hide', 'Vehículo ocultado del sitio.')}>
                            ↓ Ocultar
                          </button>
                      }
                      {role === 'ADMIN' && (
                        <button className="btn-reject" disabled={acting === v.id} onClick={() => del(v.id)}
                          style={{ background: '#7f1d1d', color: '#fff' }}>
                          ✕ Eliminar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {pages > 1 && (
            <div className="adm-pagination">
              <button className="btn-page" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Anterior</button>
              <span className="adm-page-info">Página {page + 1} de {pages}</span>
              <button className="btn-page" disabled={page === pages - 1} onClick={() => setPage(p => p + 1)}>Siguiente ›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const PAGE_SIZE = 20;

// ── Tabla de publicaciones ────────────────────────────────────────────────────
function ListingsPanel({ role }) {
  const [listings, setListings]   = useState([]);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [brandFilter, setBrandFilter]   = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const [page, setPage]                 = useState(1);
  const [loading, setLoading]           = useState(true);
  const [acting, setActing]             = useState(null);
  const [preview, setPreview]           = useState(null);
  const [editing, setEditing]           = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authApi.get('/api/admin/listings');
      setListings(res.data);
    } catch { setListings([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // reset página cuando cambian filtros
  useEffect(() => { setPage(1); }, [statusFilter, brandFilter, dateFrom, dateTo]);

  const action = async (id, endpoint) => {
    setActing(id);
    try {
      await authApi.post(`/api/admin/listings/${id}/${endpoint}`);
      load();
    } finally { setActing(null); }
  };

  const changeStatus = async (id, status) => {
    setActing(id);
    try {
      await authApi.put(`/api/admin/listings/${id}/status`, null, { params: { status } });
      load();
    } finally { setActing(null); }
  };

  // ── Filtrado ──
  const filtered = listings.filter(l => {
    if (l.deletedAt) return false;
    if (statusFilter !== 'ALL' && l.status !== statusFilter) return false;
    if (brandFilter.trim()) {
      const b = brandFilter.trim().toLowerCase();
      const name = [l.makerName, l.modelName].filter(Boolean).join(' ').toLowerCase();
      if (!name.includes(b)) return false;
    }
    if (dateFrom && l.createdAt && new Date(l.createdAt) < new Date(dateFrom)) return false;
    if (dateTo   && l.createdAt && new Date(l.createdAt) > new Date(dateTo + 'T23:59:59')) return false;
    return true;
  });

  const pendingCount = listings.filter(l => l.status === 'PENDING').length;
  const totalPages   = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated    = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="adm-section">

      {/* Filtros de estado */}
      <div className="adm-filter-row">
        {['PENDING', 'ACTIVE', 'REJECTED', 'ALL'].map(f => (
          <button
            key={f}
            className={`btn-filter ${statusFilter === f ? 'active' : ''}`}
            onClick={() => setStatusFilter(f)}
          >
            {f === 'ALL' ? 'Todas' : STATUS_LABEL[f]?.label}
            {f === 'PENDING' && pendingCount > 0 && statusFilter !== 'PENDING' &&
              <span className="filter-count">{pendingCount}</span>
            }
          </button>
        ))}
      </div>

      {/* Filtros adicionales */}
      <div className="adm-extra-filters">
        <input
          type="text"
          placeholder="Buscar por marca o modelo..."
          value={brandFilter}
          onChange={e => setBrandFilter(e.target.value)}
        />
        <div className="adm-date-range">
          <label>Desde</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <label>Hasta</label>
          <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)} />
          {(dateFrom || dateTo) &&
            <button className="btn-clear" onClick={() => { setDateFrom(''); setDateTo(''); }}>✕</button>
          }
        </div>
        <span className="adm-count">{filtered.length} resultado{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {preview && <ListingPreviewModal listing={preview} onClose={() => setPreview(null)} />}
      {editing && <EditListingModal listing={editing} onClose={() => setEditing(null)} onSaved={load} />}

      {loading ? (
        <div className="adm-spinner-wrap"><div className="adm-spinner" /></div>
      ) : paginated.length === 0 ? (
        <div className="adm-empty">No hay publicaciones con estos filtros.</div>
      ) : (
        <>
        <table className="adm-table">
          <thead>
            <tr>
              <th>ID</th><th>Vehículo</th><th>Precio</th><th>Contacto</th>
              <th>Estado</th><th>Fecha</th><th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map(l => (
              <tr key={l.id}>
                <td>#{l.id}</td>
                <td>
                  <strong>{[l.makerName, l.modelName, l.year].filter(Boolean).join(' ') || 'Sin título'}</strong>
                  {l.city && <div style={{ fontSize: 12, color: '#888' }}>{l.city}</div>}
                </td>
                <td>{formatPrice(l.price)}</td>
                <td>
                  <div style={{ fontSize: 13 }}>{l.contactName}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{l.contactPhone}</div>
                </td>
                <td><StatusBadge status={l.status} /></td>
                <td style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>
                  {l.createdAt ? new Date(l.createdAt).toLocaleDateString('es-CL') : '—'}
                </td>
                <td>
                  <div className="adm-actions">
                    <button className="btn-preview" onClick={() => setPreview(l)}>👁 Ver</button>
                    <button className="btn-preview" onClick={() => setEditing(l)}>✏️ Editar</button>
                    {l.status === 'PENDING' && (
                      <>
                        <button className="btn-approve" disabled={acting === l.id} onClick={() => action(l.id, 'approve')}>✓ Aprobar</button>
                        <button className="btn-reject"  disabled={acting === l.id} onClick={() => action(l.id, 'reject')}>✕ Rechazar</button>
                      </>
                    )}
                    {l.status !== 'PENDING' && role === 'ADMIN' && (
                      <select className="select-status" value={l.status} onChange={e => changeStatus(l.id, e.target.value)} disabled={acting === l.id}>
                        <option value="PENDING">Pendiente</option>
                        <option value="ACTIVE">Activa</option>
                        <option value="REJECTED">Rechazada</option>
                      </select>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="adm-pagination">
            <button className="btn-page" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Anterior</button>
            <span className="adm-page-info">Página {page} de {totalPages}</span>
            <button className="btn-page" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Siguiente ›</button>
          </div>
        )}
        </>
      )}
    </div>
  );
}

// ── Panel de Proveedores ──────────────────────────────────────────────────────
function ProvidersPanel() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(null);

  useEffect(() => {
    authApi.get('/api/admin/providers')
      .then(r => setProviders(r.data))
      .catch(e => console.error('Error cargando proveedores:', e))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (provider) => {
    setSaving(provider.id);
    try {
      const r = await authApi.put(`/api/admin/providers/${provider.id}/enabled`, null, {
        params: { enabled: !provider.enabled }
      });
      setProviders(prev => prev.map(p => p.id === provider.id ? { ...p, enabled: r.data.enabled } : p));
    } catch (e) {
      console.error('Error actualizando proveedor:', e);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <div className="adm-empty">Cargando proveedores…</div>;

  return (
    <div className="adm-section">
      <h3 className="adm-section-title">Proveedores de scraping</h3>
      <p className="adm-section-subtitle">
        Los proveedores deshabilitados no aparecerán en las búsquedas.
      </p>
      <div className="providers-list">
        {providers.map(p => (
          <div key={p.id} className={`provider-row ${p.enabled ? '' : 'provider-disabled'}`}>
            <div className="provider-info">
              <span
                className="provider-badge"
                style={{ background: p.bgColor, color: p.textColor }}
              >
                {p.label}
              </span>
              <span className="provider-name">{p.name}</span>
            </div>
            <div className="provider-actions">
              <span className={`adm-badge ${p.enabled ? 'badge-active' : 'badge-rejected'}`}>
                {p.enabled ? 'Activo' : 'Deshabilitado'}
              </span>
              <button
                className={`btn-adm-sm ${p.enabled ? 'btn-adm-danger' : 'btn-adm-primary'}`}
                disabled={saving === p.id}
                onClick={() => toggle(p)}
              >
                {saving === p.id ? '…' : p.enabled ? 'Deshabilitar' : 'Habilitar'}
              </button>
            </div>
          </div>
        ))}
        {providers.length === 0 && (
          <div className="adm-empty">No hay proveedores configurados.</div>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function AdminPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('listings');

  if (!user) {
    return (
      <div className="adm-page">
        <div className="adm-empty" style={{ padding: 60 }}>
          <h3>Debes iniciar sesión para acceder.</h3>
        </div>
      </div>
    );
  }

  if (role !== 'ADMIN' && role !== 'REVIEWER') {
    return (
      <div className="adm-page">
        <div className="adm-empty" style={{ padding: 60 }}>
          <h3>Sin permisos de acceso.</h3>
          <button className="btn-adm-primary" onClick={() => navigate('/')}>Volver al inicio</button>
        </div>
      </div>
    );
  }

  return (
    <div className="adm-page">
      <div className="adm-header">
        <div>
          <h2>Panel de administración</h2>
          <span className={`adm-badge ${role === 'ADMIN' ? 'badge-active' : 'badge-pending'}`}>
            {role === 'ADMIN' ? 'Administrador' : 'Revisor'}
          </span>
        </div>
      </div>

      <div className="adm-tabs">
        <button className={tab === 'listings' ? 'active' : ''} onClick={() => setTab('listings')}>
          Publicaciones
        </button>
        <button className={tab === 'scraped' ? 'active' : ''} onClick={() => setTab('scraped')}>
          Publicaciones scrapeadas
        </button>
        {role === 'ADMIN' && (
          <button className={tab === 'providers' ? 'active' : ''} onClick={() => setTab('providers')}>
            Proveedores
          </button>
        )}
        {role === 'ADMIN' && (
          <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>
            Usuarios
          </button>
        )}
      </div>

      {tab === 'listings'  && <ListingsPanel role={role} />}
      {tab === 'scraped'   && <ScrapedPanel  role={role} />}
      {tab === 'providers' && <ProvidersPanel />}
      {tab === 'users'     && <UsersPanel />}
    </div>
  );
}
